import { gql } from '@common/hooks';
import {
  CommentWithFileInfoFragment,
  CommentWithFileInfoFragmentType,
  DisputeStateType,
  DisputeType,
  HistoryInfoFragment,
  HistoryInfoFragmentType,
  SimpleFileFragmentType,
  StaffingRequestType,
} from '@common/graphql';

export type DisputeAvailableActionType =
  | 'review'
  | 'reopen'
  | 'reject'
  | 'approve'
  | 'accept'
  | 'edit'
  | 'redirect';

export const DisputeRejectionReasonFragment = gql`
  fragment DisputeRejectionReasonFragment on DisputeRejectionReason {
    id
    name
    commentRequired
  }
`;

export type DisputeRejectionReasonFragmentType = {
  id: string;
  name: string;
  commentRequired: boolean;
};

export const DisputeListItemFragment = gql`
  fragment DisputeListItemFragment on Dispute {
    id
    code
    state
    newDate
    newHours
    newTotalSeconds
    newPayCode
    createdAt
    creationReason
    createdBy {
      id
      entityType
    }
    updatedBy {
      id
      entityType
    }
    disputeType
    employeeId
    needAction
    staffingRequestType
    lastActionAt
    exported
    canReopen {
      value
    }
    canApprove {
      value
    }
    canRetract {
      value
    }
    canReject {
      value
    }
    canAccept {
      value
    }
    canEdit {
      value
    }
    canRedirectToOrganization {
      value
    }
    canRedirectToSupplier {
      value
    }
    canRetractRedirect {
      value
    }
    supplier {
      id
      name
    }
    worksInvoice {
      id
      code
    }
    worksReverseInvoice {
      id
      code
    }
    staffingRequestAssignment {
      id
      worksProfile {
        id
        fullName
      }
    }
    organizationUnit {
      id
      costCenter
    }
  }
`;

export type DisputeListItemFragmentType = {
  id: string;
  code: string;
  state: DisputeStateType;
  newDate: string;
  newHours: number;
  newPayCode: string;
  creationReason: string;
  createdBy: {
    entityType: 'Supplier' | 'Organization';
  } | null;
  updatedBy: {
    entityType: 'Supplier' | 'Organization';
  } | null;
  employeeId: string;
  disputeType: DisputeType;
  needAction: boolean;
  staffingRequestType: StaffingRequestType;
  createdAt: string;
  lastActionAt: string;
  exported: boolean;
  canReopen: {
    value: boolean;
  };
  canApprove: {
    value: boolean;
  };
  canRetract: {
    value: boolean;
  };
  canReject: {
    value: boolean;
  };
  canEdit: {
    value: boolean;
  };
  canRedirectToOrganization: {
    value: boolean;
  };
  canRedirectToSupplier: {
    value: boolean;
  };
  canRetractRedirect: {
    value: boolean;
  };
  canAccept: {
    value: boolean;
  };
  supplier: {
    id: string;
    name: string;
  };
  worksInvoice: {
    id: string;
    code: string;
  };
  worksReverseInvoice: {
    id: string;
    code: string;
  };
  staffingRequestAssignment: {
    id: string;
    worksProfile: {
      id: string;
      fullName: string;
    };
  };
  organizationUnit: {
    id: string;
    costCenter: string;
  };
};

export const DisputeReviewFragment = gql`
  fragment DisputeReviewFragment on Dispute {
    id
    code
    state
    newDate
    newHours
    newTotalSeconds
    newPayCode
    creationReason
    disputeType
    employeeId
    staffingRequestType
    finalRateCents
    billingAmountCents
    lastAction
    lastUserAction
    hasCustomRate
    rejectionReason {
      ...DisputeRejectionReasonFragment
    }
    createdBy {
      id
      entityType
    }
    updatedBy {
      id
      entityType
    }
    supplier {
      id
      name
    }
    organization {
      id
      name
      payCodes
    }
    worksInvoice {
      id
      code
      preCalculated
    }
    worksReverseInvoice {
      id
      code
    }
    resolvingInvoice {
      id
      code
    }
    resolvingReverseInvoice {
      id
      code
    }
    staffingRequestAssignment {
      id
      billingRegularRate
      worksProfile {
        id
        fullName
      }
    }
    organizationUnit {
      id
      name
    }
    canReopen {
      value
    }
    canApprove {
      value
    }
    canRetract {
      value
    }
    canReject {
      value
    }
    canAccept {
      value
    }
    canEdit {
      value
    }
    canRedirectToOrganization {
      value
    }
    canRedirectToSupplier {
      value
    }
    canRetractRedirect {
      value
    }
    canChangeBillingAmount {
      value
    }
    worksInvoiceItem {
      id
      employeeId
      effectiveHourlyRateCents
      payCode
      hours
      totalSeconds
      worksProfile {
        id
        fullName
      }
    }
    files {
      organization {
        ...SimpleFileFragment
      }
      supplier {
        ...SimpleFileFragment
      }
    }
    history {
      ...HistoryInfoFragment
    }
    lastComment {
      ...CommentWithFileInfoFragment
    }
  }
  ${HistoryInfoFragment}
  ${CommentWithFileInfoFragment}
  ${DisputeRejectionReasonFragment}
`;

export type DisputeReviewFragmentType = {
  id: string;
  code: string;
  state: DisputeStateType;
  newDate: string;
  newHours: number;
  newTotalSeconds: number;
  newPayCode: string;
  creationReason?: string;
  rejectionReason?: DisputeRejectionReasonFragmentType;
  employeeId: string;
  hasCustomRate: boolean;
  lastAction:
    | 'approve'
    | 'edit'
    | 'accept'
    | 'retract'
    | 'reject'
    | 'reopen'
    | 'manually_resolve'
    | null;
  lastUserAction:
    | 'approve'
    | 'edit'
    | 'accept'
    | 'retract'
    | 'reject'
    | 'open'
    | null;

  // FIXME(financials): wrong nullability for these at gql
  finalRateCents: number;
  billingAmountCents: number;
  disputeType: DisputeType;

  staffingRequestType: string;
  createdBy: {
    entityType: 'Supplier' | 'Organization';
  } | null;
  updatedBy: {
    entityType: 'Supplier' | 'Organization';
  } | null;
  history: HistoryInfoFragmentType[];
  files: {
    supplier: SimpleFileFragmentType[];
    organization: SimpleFileFragmentType[];
  };
  supplier: {
    id: string;
    name: string;
  };
  organization: {
    id: string;
    name: string;
    payCodes: string[];
  };
  worksInvoice: {
    id: string;
    code: string;
    preCalculated: boolean;
  };
  worksReverseInvoice: {
    id: string;
    code: string;
  };
  resolvingInvoice: {
    id: string;
    code: string;
  };
  resolvingReverseInvoice: {
    id: string;
    code: string;
  };
  worksInvoiceItem?: {
    employeeId: string | undefined;
    effectiveHourlyRateCents: number;
    payCode?: string;
    hours: number;
    totalSeconds: number;
    worksProfile: {
      fullName: string;
    };
  };
  staffingRequestAssignment: {
    id: string;
    billingRegularRate: number;
    worksProfile: {
      id: string;
      fullName: string;
    };
  };
  organizationUnit: {
    id: string;
    name: string;
  };
  canReopen: {
    value: boolean;
  };
  canApprove: {
    value: boolean;
  };
  canRetract: {
    value: boolean;
  };
  canReject: {
    value: boolean;
  };
  canEdit: {
    value: boolean;
  };
  canAccept: {
    value: boolean;
  };
  canRedirectToOrganization: {
    value: boolean;
  };
  canRedirectToSupplier: {
    value: boolean;
  };
  canRetractRedirect: {
    value: boolean;
  };
  canChangeBillingAmount: {
    value: boolean;
  };
  lastComment: CommentWithFileInfoFragmentType | null;
};

export const InvoiceItemListDisputeFragment = gql`
  fragment ListInvoiceItemDisputeFragment on Dispute {
    id
    code
    state
    createdBy {
      id
      entityType
    }
    updatedBy {
      id
      entityType
    }
    canReopen {
      value
    }
    canApprove {
      value
    }
    canRetract {
      value
    }
    canReject {
      value
    }
    canAccept {
      value
    }
    canEdit {
      value
    }
    canRedirectToOrganization {
      value
    }
    canRedirectToSupplier {
      value
    }
    canRetractRedirect {
      value
    }
  }
`;

export type InvoiceItemListDisputeFragmentType = {
  id: string;
  code: string;
  state: DisputeStateType;
  createdBy: {
    entityType: 'Supplier' | 'Organization';
  } | null;
  updatedBy: {
    entityType: 'Supplier' | 'Organization';
  } | null;
  canReopen: {
    value: boolean;
  };
  canApprove: {
    value: boolean;
  };
  canRetract: {
    value: boolean;
  };
  canReject: {
    value: boolean;
  };
  canEdit: {
    value: boolean;
  };
  canRedirectToOrganization: {
    value: boolean;
  };
  canRedirectToSupplier: {
    value: boolean;
  };
  canRetractRedirect: {
    value: boolean;
  };
  canAccept: {
    value: boolean;
  };
};
